import { AppManager } from "@/services/AppManager";
import * as Sentry from "@sentry/browser";

// Tipo para o contexto adicional
type LogContext = Record<string, unknown>;

type LogLevel = "info" | "warning" | "error";

// Obtém a variável de ambiente do Vite
const ENVIRONMENT = import.meta.env.VITE_APP_ENV || "dev";

class Logger {
  /**
   * Envia uma mensagem para o Sentry com um nível específico.
   * @param level O nível do log ('info', 'warning', 'error').
   * @param message A mensagem a ser registrada.
   * @param context Contexto adicional para enriquecer o log.
   */
  private static log(
    level: LogLevel,
    message: string,
    context: LogContext = {}
  ): void {
    Sentry.withScope((scope) => {
      // Adiciona a variável de ambiente ao contexto
      scope.setContext("env", { environment: ENVIRONMENT });
      const user = AppManager.getInstance().getUser();
      if (user) {
        scope.setContext("userData", { user: JSON.stringify(user) });
      }

      // Adiciona o contexto adicional, se houver
      if (Object.keys(context).length > 0) {
        scope.setContext(level, context);
      }

      // Envia o log para o Sentry
      Sentry.captureMessage(message, level);
    });
  }

  static info(message: string, context: LogContext = {}): void {
    this.log("info", message, context);
  }

  static warning(message: string, context: LogContext = {}): void {
    this.log("warning", message, context);
  }

  static error(message: string, context: LogContext = {}): void {
    this.log("error", message, context);
  }

  static exception(error: Error, context: LogContext = {}): void {
    Sentry.withScope((scope) => {
      // Adiciona a variável de ambiente ao contexto
      scope.setContext("env", { environment: ENVIRONMENT });

      // Adiciona o contexto adicional, se houver
      if (Object.keys(context).length > 0) {
        scope.setContext("exception", context);
      }

      // Envia a exceção para o Sentry
      Sentry.captureException(error);
    });
  }
}

export default Logger;
